import { Check, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { absoluteTime, humanizeEnum, relativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { ALERT_ICON, SeverityBadge } from './severity-badge';
import type { Alert } from '@/types/api';

export function AlertRow({
  alert,
  plateNumber,
  compact = false,
  selected,
  onSelectedChange,
  onAcknowledge,
  onLocate,
  onOpen,
  className,
}: {
  alert: Alert;
  plateNumber?: string;
  compact?: boolean;
  selected?: boolean;
  onSelectedChange?: (checked: boolean) => void;
  onAcknowledge?: () => void;
  onLocate?: () => void;
  onOpen?: () => void;
  className?: string;
}) {
  const Icon = ALERT_ICON[alert.type] ?? MapPin;
  const isOpen = alert.status === 'OPEN';

  return (
    <div
      className={cn(
        'group flex items-start gap-3 px-3 py-2.5 transition-colors',
        onOpen && 'cursor-pointer hover:bg-accent/45',
        !isOpen && 'opacity-70',
        className,
      )}
      onClick={onOpen}
    >
      {onSelectedChange && (
        <Checkbox
          checked={selected}
          onCheckedChange={(value) => onSelectedChange(value === true)}
          onClick={(event) => event.stopPropagation()}
          className="mt-1"
          aria-label={`Select ${alert.title}`}
        />
      )}

      <span
        className={cn(
          'mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-sm border',
          alert.severity === 'CRITICAL'
            ? 'border-severity-critical/30 bg-severity-critical/10 text-severity-critical'
            : alert.severity === 'WARNING'
              ? 'border-severity-warning/30 bg-severity-warning/10 text-severity-warning'
              : 'border-severity-info/30 bg-severity-info/10 text-severity-info',
        )}
      >
        <Icon className="size-3.5" strokeWidth={2} />
      </span>

      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-baseline gap-2">
          <p className="truncate text-[13px] font-medium">{alert.title}</p>
          {!compact && <SeverityBadge severity={alert.severity} />}
          {alert.status === 'ACKNOWLEDGED' && (
            <span className="label-micro shrink-0 text-[9px]">Ack'd</span>
          )}
        </div>

        <p className={cn('text-xs leading-relaxed text-muted-foreground', compact && 'line-clamp-2')}>
          {alert.message}
        </p>

        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-muted-foreground">
          {plateNumber && <span className="tnum font-medium text-foreground">{plateNumber}</span>}
          <span className="tnum">{humanizeEnum(alert.type)}</span>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="tnum">{relativeTime(alert.occurredAt)}</span>
            </TooltipTrigger>
            <TooltipContent side="bottom">{absoluteTime(alert.occurredAt)}</TooltipContent>
          </Tooltip>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
        {onLocate && alert.position && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                onClick={(event) => {
                  event.stopPropagation();
                  onLocate();
                }}
              >
                <MapPin className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Show on map</TooltipContent>
          </Tooltip>
        )}
        {onAcknowledge && isOpen && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                onClick={(event) => {
                  event.stopPropagation();
                  onAcknowledge();
                }}
              >
                <Check className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Acknowledge</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
